export type {
  Product,
  VariantAttribute,
  VariantAttributeValue,
  ProductVariantsResponse,
  CreateVariantRow,
  CreateVariantsPayload,
} from '@bbpos/types';
import type { Product, CreateVariantRow } from '@bbpos/types';

export type GstRate = 0 | 5 | 10 | 12 | 15 | 18 | 28;

/** What Receive Goods captures for a product: quantity only, batch & expiry, or one serial per unit. */
export type TrackingMode = 'NONE' | 'BATCH' | 'SERIAL';
/** Per-product override; DEFAULT inherits the tenant's inventory tracking setting. */
export type TrackingOverride = TrackingMode | 'DEFAULT';

export const TRACKING_OPTIONS: { value: TrackingOverride; label: string }[] = [
  { value: 'DEFAULT', label: 'Default (business setting)' },
  { value: 'NONE', label: 'Quantity only' },
  { value: 'BATCH', label: 'Batch & expiry' },
  { value: 'SERIAL', label: 'Serial numbers' },
];

export const TRACKING_LABELS: Record<TrackingMode, string> = {
  NONE: 'Quantity only',
  BATCH: 'Batch & expiry',
  SERIAL: 'Serial numbers',
};

export type SerialStatus = 'IN_STOCK' | 'SOLD' | 'RETURNED' | 'WRITTEN_OFF';

export interface ProductSerial {
  id: string;
  serial_number: string;
  status: SerialStatus;
  location_id: string | null;
  location_name: string | null;
  received_at: string;
  notes: string | null;
  purchase_order_id: string | null;
  po_number: string | null;
  grn_number: string | null;
}

export type StockStatus = 'in_stock' | 'low_stock' | 'out_of_stock';

export interface ProductListResponse {
  data: (Product & { id: string })[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface CreateProductPayload {
  name: string;
  sku: string;
  barcode?: string;
  category?: string;
  unit: string;
  price: number;
  cost: number;
  gst_rate: GstRate;
  purchase_gst_rate?: GstRate;
  tracking?: TrackingOverride;
  /** Per-product commission percentage 0–100 (retail only). */
  commission_pct?: number;
  hsn_code?: string;
  /** Free-text brand name. */
  brand?: string;
  /** 'GOODS' (stocked) or 'SERVICE' (not inventory-tracked). */
  product_type?: string;
  stock_quantity?: number;
  low_stock_threshold?: number;
  is_active?: boolean;
  /** TRUE while this is an unfinished wizard draft (hidden from POS). */
  is_draft?: boolean;
  /** Per-module visibility, set on the parent (variants inherit). */
  hide_in_pos?: boolean;
  hide_in_sales?: boolean;
  description?: string;
  image_url?: string;
  location_id?: string;
  /** The other stores to sell it at, each with its own opening stock (`location_id` is the first). */
  stocks?: Array<{ location_id: string; quantity: number }>;
  expiry_date?: string;
  /** Variants saved with the product in one transaction — all or nothing. */
  variants?: CreateVariantRow[];
}

export interface UpdateProductPayload {
  name?: string;
  sku?: string;
  barcode?: string;
  category?: string;
  unit?: string;
  price?: number;
  /**
   * Variants only: signed delta in paise added to the parent's price. The API
   * derives the effective `price` from it, so send one or the other.
   */
  price_adjustment?: number;
  cost?: number;
  gst_rate?: GstRate;
  purchase_gst_rate?: GstRate;
  tracking?: TrackingOverride;
  /** Per-product commission percentage 0–100 (retail only). */
  commission_pct?: number;
  hsn_code?: string;
  /** Free-text brand name. */
  brand?: string;
  /** 'GOODS' (stocked) or 'SERVICE' (not inventory-tracked). */
  product_type?: string;
  low_stock_threshold?: number;
  is_active?: boolean;
  /** Set FALSE to finish a draft, making it an ordinary product. */
  is_draft?: boolean;
  /** Per-module visibility, set on the parent (variants inherit). */
  hide_in_pos?: boolean;
  hide_in_sales?: boolean;
  description?: string;
  image_url?: string;
  expiry_date?: string;
}

export type StockAdjustmentType =
  | 'PURCHASE'
  | 'ADJUSTMENT'
  | 'DAMAGE'
  | 'OPENING_STOCK'
  | 'WASTAGE'
  | 'EXPIRY_WRITE_OFF';

export interface StockAdjustmentPayload {
  type: StockAdjustmentType;
  quantity?: number;
  unit_cost?: number;
  notes?: string;
  /** Stock-decreasing types only: write off this specific batch (FEFO when omitted). */
  batch_id?: string;
  /**
   * Adjust the row at this location. Products are location-scoped rows sharing a
   * SKU, so without this the adjustment lands on whichever row the URL id names.
   * Omitted ⇒ that id's own location.
   */
  location_id?: string;
  /** ADJUSTMENT only: set stock to this physical count (sent instead of quantity). */
  counted_quantity?: number;
}

/** Open batch (quantity > 0) of a product at its location — GET /products/:id/batches. */
export interface ProductBatch {
  id: string;
  batch_number: string;
  expiry_date: string; // YYYY-MM-DD
  quantity: number;
  location_id: string;
}

export interface StockTransferPayload {
  target_location_id: string;
  quantity: number;
  notes?: string;
}

export interface StockHistoryItem {
  id: string;
  product_id: string;
  location_id: string;
  type: string;
  quantity: number;
  unit_cost: number;
  reference_id?: string;
  notes?: string;
  created_at: string;
  created_by?: string;
  /** Name of the row the movement happened on — a parent's history includes its variants'. */
  product_name?: string;
  /** Who made the movement (created_by is their user id). */
  created_by_name?: string | null;
}

/** GET /products/:id/edit-info — unrecorded edits are credited to the business owner. */
export interface ProductEditInfo {
  created_at: string;
  created_by_name: string | null;
  updated_at: string;
  updated_by_name: string | null;
}

/** One catalogue edit of a product or one of its variants — GET /products/:id/change-log. */
export interface ProductChangeLogEntry {
  id: string;
  created_at: string;
  action: string;
  path: string;
  resource_id: string;
  user_name: string | null;
  product_name: string | null;
  changes: Record<string, unknown> | null;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
}

export interface StockHistoryResponse {
  data: StockHistoryItem[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface LocationStock {
  location_id: string;
  location_name: string;
  stock_quantity: number;
  low_stock_threshold: number;
}

/** One variant's stock at one location — GET /products/:id/variant-stock. */
export interface VariantLocationStock extends LocationStock {
  product_id: string;
  name: string;
  sku: string;
  variant_attributes: Record<string, string> | null;
  is_active: boolean;
}

/** Units on a variant parent's own row at one location — not sellable until assigned. */
export interface UnallocatedStock {
  product_id: string;
  location_id: string;
  location_name: string;
  stock_quantity: number;
}

export interface VariantStockResponse {
  variants: VariantLocationStock[];
  unallocated: UnallocatedStock[];
}

export interface AssignVariantStockPayload {
  variant_id: string;
  location_id: string;
  quantity: number;
  notes?: string;
}

export interface ImportPreviewRow {
  row: number;
  name: string;
  sku: string;
  category?: string;
  price?: number;
  stock?: number;
  valid: boolean;
  errors?: string[];
}

export interface ProductImportResponse {
  job_id: string;
  total: number;
  imported: number;
  /** Of `imported`: rows that made a new product master / rows of an existing one. */
  created?: number;
  updated?: number;
  /** Rows whose SKU already exists, left as they are (no "update existing products" tick). */
  skipped?: number;
  /** Of `imported`: rows of an existing product that matched it already (nothing to change). */
  unchanged?: number;
  errors: number;
  /** Each row the server refused, with the CSV row number (header = row 1). */
  error_details?: Array<{ row: number; field: string; message: string }> | null;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  total_rows?: number;
  processed_rows?: number;
  /** What the import did — or, for a dry run, would do. Null for an import from before the master catalogue. */
  report?: ProductImportReport | null;
}

/** The stores an import adds products to: their ids, or every store the user may use. */
export type ImportStores = string[] | 'ALL';

export interface ImportReportStore {
  location_id: string;
  location_code: string | null;
  location_name: string | null;
}

/** GET /products/import/:id → report (API ImportReport). Money values are in minor units. */
export interface ProductImportReport {
  dry_run: boolean;
  stores: ImportReportStore[];
  /** Dry run only: stores the user is not assigned to; nothing is added there. */
  refused_stores: ImportReportStore[];
  new_masters: number;
  updated_masters: number;
  unchanged_masters: number;
  /** Rows whose SKU already exists, skipped because "Update existing products" was not ticked (OD7). Absent on older jobs. */
  skipped?: Array<{ row: number; sku: string }>;
  /** Per store: new store rows, rows switched back on, rows already sold there. */
  enables: Array<ImportReportStore & { created: number; enabled: number; already: number }>;
  /** A name or GST change to an existing product, or a change that was not applied (a manager's). */
  conflicts: Array<{ row: number; sku: string; field: string; current: unknown; incoming: unknown; applied: boolean }>;
  /** Store rows that already existed, so the row's opening stock was not added. */
  opening_stock_skipped: number;
}

// ─── Inventory Types ──────────────────────────────────────────────────────────

export interface ValuationItem {
  id: string;
  name: string;
  sku: string;
  category: string;
  stock_quantity: number;
  unit: string;
  wac_per_unit: number;
  total_value: number;
}

export interface ValuationResponse {
  data: ValuationItem[];
  summary: {
    total_value: number;
    total_skus: number;
    avg_cost_per_unit: number;
    /** Value of goods dispatched but not yet received (paise) — in neither branch's stock. */
    in_transit_value?: number;
    category_breakdown?: { category: string; total_value: number }[];
  };
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface StockMovement {
  id: string;
  product_id: string;
  product_name: string;
  sku: string;
  location_id: string;
  location_name?: string;
  quantity_change: number;
  type: string;
  reason?: string;
  created_at: string;
  created_by?: string;
}

export interface StockMovementsResponse {
  /** Latest 200 movements (server-capped), newest first. */
  data: StockMovement[];
  summary: {
    total_inward: number;
    total_outward: number;
    total_inward_value?: number;
    total_outward_value?: number;
  };
  by_type?: { type: string; count: number; total_quantity: number; total_value: number }[];
  daily?: { date: string; inward: number; outward: number }[];
}

export interface ExpiryAlertItem {
  id: string;
  product_id: string;
  product_name: string;
  sku: string;
  batch_number?: string;
  expiry_date: string;
  days_remaining: number;
  stock_quantity: number;
  unit: string;
  location_name?: string;
}

/** GET /products/reports/dead-stock row. Money in paise; stock_quantity is NUMERIC (string from pg). */
export interface DeadStockItem {
  id: string;
  name: string;
  sku: string;
  stock_quantity: number | string;
  cost: number;
  stock_value: number;
  last_sold_at: string | null;
  days_since_sale: number | null;
}

export interface ExpiryAlertsResponse {
  data: ExpiryAlertItem[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}
